import React, { useCallback, useEffect, useState } from "react";
import Modal from "../../components/Modal";
import Loader, { Spinner } from "../../components/Loader";
import { errorMessage } from "../../services";

const EMPTY = { title: "", excerpt: "", body: "", coverImage: "", published: true };
const when = (value) => (value ? new Date(value).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "");

// Writing a blog: ownezzy.com's (Super Admin -> Blog) or a store's (website editor -> Blog). `api` holds the calls
// (services/blog.js), `siteUrl` the public address the posts are read at, and `CoverUpload` how a cover is uploaded:
// a component given onUploaded(url) and notify.
export default function BlogManager({ api, siteUrl, notify, CoverUpload, bodyHelp = "Leave an empty line between paragraphs." }) {
  const [posts, setPosts] = useState(null);
  const [editing, setEditing] = useState(null); // the post being written: EMPTY for a new one
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.list().then(setPosts).catch((err) => notify(errorMessage(err), "error")), [api, notify]);
  useEffect(() => { load(); }, [load]);

  const set = (key) => (e) => setEditing((p) => ({ ...p, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fields = { title: editing.title, excerpt: editing.excerpt, body: editing.body, coverImage: editing.coverImage, published: editing.published };
      const { message } = editing._id ? await api.update(editing._id, fields) : await api.create(fields);
      notify(message);
      setEditing(null);
      await load();
    } catch (err) {
      notify(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    setBusy(true);
    try {
      notify((await api.remove(deleting._id)).message);
      setDeleting(null);
      await load();
    } catch (err) {
      notify(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="blog-manager">
      <button className="primary-btn" onClick={() => setEditing(EMPTY)}>＋ New post</button>
      {!posts && <Loader label="Loading posts" />}
      {posts && !posts.length && <p className="field-help">No posts yet. Published posts show on the website's Blog page.</p>}
      {posts && posts.length > 0 && (
        <ul className="blog-list">
          {posts.map((post) => (
            <li key={post._id}>
              {post.coverImage ? <img src={post.coverImage} alt="" /> : <span className="blog-noimg" aria-hidden="true">✎</span>}
              <div>
                <b>{post.title}</b>
                <small>{post.published ? `Published ${when(post.publishedAt)}` : "Draft"} · /blog/{post.slug}</small>
              </div>
              <div className="row-actions">
                {post.published && siteUrl && <a className="toggle-action" href={`${siteUrl}/blog/${post.slug}`} target="_blank" rel="noopener noreferrer">View</a>}
                <button className="edit-action" onClick={() => setEditing({ ...EMPTY, ...post })}>Edit</button>
                <button className="toggle-action blog-delete" onClick={() => setDeleting(post)}>Delete</button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <Modal eyebrow="BLOG" title={editing._id ? "Edit post" : "New post"} onClose={() => setEditing(null)} wide>
          <form onSubmit={save} className="blog-form">
            <label className="admin-field">Title<input required minLength={3} maxLength={150} value={editing.title} onChange={set("title")} /></label>
            <label className="admin-field">Short description <small className="field-help">Shown on the list of posts (up to 300 letters). Empty: the start of the post.</small><textarea rows={2} maxLength={300} value={editing.excerpt} onChange={set("excerpt")} /></label>
            <label className="admin-field">Cover image URL<input value={editing.coverImage} onChange={set("coverImage")} placeholder="https://example.com/cover.jpg" /></label>
            <CoverUpload notify={notify} onUploaded={(url) => setEditing((p) => ({ ...p, coverImage: url }))} />
            {editing.coverImage && <div className="image-url-preview"><img src={editing.coverImage} alt="" /><span>Cover preview</span></div>}
            <label className="admin-field">Post <small className="field-help">{bodyHelp}</small><textarea rows={12} maxLength={30000} value={editing.body} onChange={set("body")} /></label>
            <label className="blog-publish"><input type="checkbox" checked={editing.published} onChange={set("published")} /> Published (untick to keep it as a draft that only the admin panel shows)</label>
            <div className="product-modal-actions">
              <button type="button" className="outline-btn" onClick={() => setEditing(null)}>Cancel</button>
              <button className="primary-btn" disabled={busy}>{busy ? <><Spinner /> Saving</> : editing.published ? "Publish" : "Save draft"}</button>
            </div>
          </form>
        </Modal>
      )}

      {deleting && (
        <Modal eyebrow="BLOG" title="Delete this post?" description={`“${deleting.title}” is removed from the website. This can't be undone.`} onClose={() => setDeleting(null)}>
          <div className="product-modal-actions">
            <button type="button" className="outline-btn" onClick={() => setDeleting(null)}>Cancel</button>
            <button type="button" className="danger-btn" disabled={busy} onClick={remove}>Delete post</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
